import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";
import { CreateOrganizationAccountResponse } from "../models/CreateOrganizationAccountResponse.model";
import { Organization } from "../models/Organization";
import { GetOrganizationMembersResponse } from "../models/member/GetOrganizationMembersResponse";
import { Policy } from "../../policies/models/Policy";
import { GuestVerificationCheckType } from "../../guests/models/Guest";
import { VerificationPackage } from "../../verification-packages/models/VerificationPackage";
import { AdminPortalAPIHttpClient } from "../../../infrastructure/http/v2/AdminPortalAPIHttpClient";
import { SearchParams } from "../../../shared/search/SearchParams";
import { DataUtility } from "../../../utilities/DataUtility";
import { isStringEmptyOrNull } from "../../../utilities/StringUtility";
import { PartnerOrganization } from "../models/PartnerOrganization";
import { NotificationService } from "../../../utilities/PortalNotificationUtility";

export interface VerificationPackageCheckType {
  [packageId: number]: GuestVerificationCheckType[];
}

@Injectable({ providedIn: 'root' })
export class OrganizationService {

  lastSearchRequest?: SearchParams;
  activeTabIndex: number = 0;

  constructor(
    private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar,
    private dataUtility: DataUtility,
    private notificationService: NotificationService
  ) { }

  async getOrganizations(status?: string): Promise<any[]> {
    try {
      const endpoint = status ? `/organizations?status=${status}` : `/organizations`;
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(endpoint));
      if (!response) {
        return Promise.reject('Get organizations response null');
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving organizations ${e}`);
    }
  }

  async getOrganizationDetail(organizationId?: number | string): Promise<Organization> {
    if (!organizationId) {
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/${organizationId}`));
      if (!response) {
        return Promise.reject(`Get organization detail response null for id ${organizationId}`);
      }

    return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving organization details for id ${organizationId} ${JSON.stringify(e)}`);
    }
  }

  async searchOrganizations(searchParams: SearchParams): Promise<any> {
    try {
      this.lastSearchRequest = searchParams;

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/organizations/search', { ...searchParams }));
      if (!response) {
        return Promise.reject(`Search organizations response null`);
      }

      return Promise.resolve({
        organizations: response.data,
        totalRecords: response.metaData.totalRecords
      });
    } catch (e) {
      return Promise.reject(`Error while searching Organizations: ${JSON.stringify(e)}`);
    }
  }

  async searchOrganizationMembers(searchParams: SearchParams): Promise<GetOrganizationMembersResponse> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/organizations/member/search', { ...searchParams }));
      if (!response) {
        return Promise.reject(`Search organization members response null`);
      }

      return Promise.resolve({
        members: response.data,
        totalRecords: response.metaData.totalRecords
      });
    } catch (e) {
      return Promise.reject(`Error while searching organization members: ${JSON.stringify(e)}`);
    }
  }

  async createOrganization(organization: any): Promise<Organization> {
    try {
      const payload: Organization = {
        id: "0",
        organization_id: 0,
        access_key: organization.accessKey,
        name: organization.legalName,
        organization_name: organization.legalName,
        org_name_to_guest: organization.displayName,
        contact_first_name: organization.contactFirstName,
        contact_last_name: organization.contactLastName,
        website: organization.websiteUrl,
        logo_id: 0,
        logo_url: organization.logoUrl,
        policy_billing_method_id: organization.policy_billing_method_id,
        verification_billing_method_id: organization.verification_billing_method_id,
        billing_contact_name: `${organization.contactFirstName} ${organization.contactLastName}`,
        billing_contact_phone_number: organization.contactPhoneNumber,
        billing_contact_email_address: organization.contactEmail,
        invoice_date: organization.invoice_date,
        status_id: organization.status_id,
        source_id: organization.source_id,
        for_guest_verification: organization.for_guest_verification,
        for_reservation: organization.for_reservation,
        sync_all_properties: organization.sync_all_properties,
        start_get_reservation_from: organization.start_get_reservation_from,
        send_check_to_safely: organization.sendCheckToSafely,
        email_guest_for_details: organization.notifyGuestForAdditionalInfo,
        notify_guest_verification: organization.notifyGuestOnVerificationComplete,
        is_receiving_alert: organization.receiveVirtualCheckInAlert,
        notification_email: organization.notificationEmail,
        auto_charge_invoice: "0",
        address: {
          address_line_1: organization.address.addressLine1,
          address_line_2: organization.address.addressLine2,
          country_id: organization.address.countryId,
          state: organization.address.state,
          city: organization.address.city,
          postal_code: organization.address.postalCode
        },
        email: {
          is_primary: 1,
          email_address: organization.contactEmail.trim()
        },
        phone: {
          is_primary: 1,
          phone_number: organization.contactPhoneNumber
        },
        members: [],
        created_date: organization.created_date
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/organizations`, payload));
      if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
        this.dataUtility.displaySnackbar(response.err.msg);
      } else {
        this.dataUtility.displaySnackbar(response.err);
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating organization ${e}`);
    }
  }

  async updateOrganization(org: any): Promise<Organization> {
    const organizationId = org.id;

    if (!organizationId) {
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/organizations/${organizationId}`, org));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving organization details for Organization ${organizationId}: ${JSON.stringify(e)}`);
    }
  }

  async updateOrganizationStatus(organization: Organization, payload: any): Promise<Organization[]> {
    const organizationId = organization.id;

    const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/organizations/update-status/${organizationId}`, payload));
    return Promise.resolve(response.data);
  }

  async createAccount({ firstName, lastName, organizationId, emailAddress, phoneNumber, requireActivation, roleId, password = null, confirmPassword = null }: any): Promise<CreateOrganizationAccountResponse> {
    try {
      const payload: any = {
        first_name: firstName,
        last_name: lastName,
        organization_id: organizationId,
        request_for_email: emailAddress.trim(),
        phone_number: phoneNumber,
        requireActivation: requireActivation,
        role_id: roleId
      };
      
      if (password && confirmPassword) {
        payload['password'] = password;
        payload['confirmPassword'] = confirmPassword;
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/organizations/request-org-account', payload));
      if (!response) {
        return Promise.reject(`Create organization account response null for email address ${emailAddress}`);
      }
      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while sending create organization account request ${emailAddress} ${JSON.stringify(e)}`);
    }
  }

  async getPoliciesByOrganizationId(organizationId: number): Promise<Policy[]> {
    if (!organizationId) {
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/policies?organization_id=${organizationId}&status_id=1`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving organization details for id ${organizationId} ${JSON.stringify(e)}`);
    }
  }

  async getAllOrganizationPropertyPolicies(): Promise<Policy[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/policies/properties`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving organization property policies: ${JSON.stringify(e)}`);
    }
  }

  async createOrganizationVerificationPackage(payload: any): Promise<VerificationPackage> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/organizations/verification-packages`, payload));
      if (!response) {
        return Promise.reject(`Create organization verification package response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating organization verification package: ${e}`);
    }
  }

  async updateOrganizationVerificationPackage(verificationPackageId: any, verificationPackage: any): Promise<VerificationPackage> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/organizations/verification-packages/${verificationPackageId}`, verificationPackage));
      if (!response) {
        return Promise.reject(`Update organization verification package response null ${verificationPackageId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating organization verification package ${verificationPackageId}: ${e}`);
    }
  }

  async getPartnerOrganizations(): Promise<PartnerOrganization[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/partner`));
      if (!response) {
        return Promise.reject('Get partner organizations response null');
      }

      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while retrieving partner organizations ${e}`);
    }
  }

  async getPartnerOrganizationById(organizationId?: string): Promise<PartnerOrganization> {
    if (!organizationId) {
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/partner/${organizationId}`));
      if (!response) {
        return Promise.reject(`Get partner organization response null for ID: ${organizationId}`);
      }

    return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while retrieving partner organization for ID: ${organizationId} ${JSON.stringify(e)}`);
    }
  }

  async updatePartnerOrganization(partnerOrganizationId: string, payload: any): Promise<PartnerOrganization> {
    if (!partnerOrganizationId) {
      return Promise.reject(`Null or undefined Organization ID`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/organizations/partner/${partnerOrganizationId}`, payload));
      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while updating partner organization for Partner Organization ID: ${partnerOrganizationId}: ${JSON.stringify(e)}`);
    }
  }

  async updateV2OrganizationStatusDate(organizationId: string, payload: any): Promise<any> {
    if (!organizationId) {
      return Promise.reject(`Null or undefined Organization ID`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/organizations/partner/v2/${organizationId}`, payload));
      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while updating Organization ID: ${organizationId}: ${JSON.stringify(e)}`);
    }
  }

  async getOrganizationConnectorById(organizationId?: string): Promise<any> {
    if (!organizationId) {
      this.notificationService.showNotification({ message: 'Null or undefined Organization ID'})
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/connectors/${organizationId}`));
      if (!response) {
        return Promise.reject(`Get organization connectors response null for ID: ${organizationId}`);
      }

      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while retrieving organization connectors for ID: ${organizationId} ${JSON.stringify(e)}`);
    }
  }

  async updatePartnerOrgOnboardingStatus(partnerOrgPayload: any): Promise<any[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/organizations/partner/onboarding/status`, partnerOrgPayload));
      if (!response) {
        return Promise.reject('Update partner organization status response null');
      }

      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while updating partner organization onboarding status: ${e}`);
    }
  }

  async getV2OrganizationDetail(organizationId?: string): Promise<any> {
    if (!organizationId) {
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/partner/v2/${organizationId}`));
      if (!response) {
        return Promise.reject(`Get partner organization response null for ID: ${organizationId}`);
      }

    return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while retrieving partner organization for ID: ${organizationId} ${JSON.stringify(e)}`);
    }
  }


  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }
    
  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Organization: ${errorMessage}`, 'OK', { duration: 4000 });
  }
}